import React, { useEffect, useState } from 'react';
import { CurrencyDollar, CheckCircle, Warning } from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * Read-only Square configuration panel for MASTER / LMASTER admins.
 * Confirms that the platform's Square account is reachable and shows
 * which mode (production / sandbox) and location are active. Webhook
 * signature key status is also surfaced — when missing, webhook
 * payment.created events are accepted unsigned.
 */
export const SquareStatusPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/payments/square/health');
        setData(data);
      } catch {
        setData({ configured: false, error: 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="bg-[#111] border border-[#1F1F1F] rounded-2xl p-6 mt-6 shimmer h-32" />;

  const ok = !!data?.configured;
  const env = data?.environment || 'unknown';
  const webhookOk = !!data?.webhook_signature_key_set;

  return (
    <div className="bg-[#111] border border-[#1F1F1F] rounded-2xl p-6 sm:p-8 mt-6" data-testid="square-status-panel">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
          <CurrencyDollar size={22} weight="duotone" className={ok ? 'text-emerald-400' : 'text-red-400'} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Square Payments</h2>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${ok ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
              {ok ? 'Connected' : 'Not configured'}
            </span>
          </div>
          <p className="text-sm text-[#9E9E9E] mt-1">
            All rider payments are routed to the platform's Square account ({env}).
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-[#9E9E9E]">Environment</span>
          <span className="font-semibold text-white uppercase">{env}</span>
        </div>
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-[#9E9E9E]">Location</span>
          <span className="font-semibold text-white truncate ml-2">{data?.location_name || data?.location_id || '—'}</span>
        </div>
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-[#9E9E9E]">Application ID</span>
          {data?.application_id_set ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Warning size={18} weight="fill" className="text-red-400" />}
        </div>
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-[#9E9E9E]">Access Token</span>
          {data?.access_token_set ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Warning size={18} weight="fill" className="text-red-400" />}
        </div>
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between sm:col-span-2">
          <div className="flex flex-col">
            <span className="text-[#9E9E9E]">Webhook Signature Key</span>
            <span className="text-[11px] text-[#6B6B6B] mt-0.5">{webhookOk ? 'Set — incoming events are verified.' : 'Not set — webhook events accepted unsigned. Add SQUARE_WEBHOOK_SIGNATURE_KEY in env to enforce verification.'}</span>
          </div>
          {webhookOk ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Warning size={18} weight="fill" className="text-amber-400" />}
        </div>
      </div>

      {data?.error && (
        <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-md px-4 py-3 text-sm text-red-300">
          <strong>Error:</strong> {data.error}
        </div>
      )}

      <p className="mt-5 text-xs text-[#6B6B6B] leading-relaxed">
        Single-merchant mode is active — all riders pay into the platform's Square account.
        Producer-direct OAuth marketplace mode is scaffolded but disabled (requires Square Application Secret + Webhook Signature Key).
      </p>
    </div>
  );
};
